<template>
  <InputSkeleton :name="name"
                 :errors="errors"
                 class="DatePickerInput"
                 :class="{
                   [`DatePickerInput--${size}`]: !!size,
                   'DatePickerInput--disabled': disabled,
                   'DatePickerInput--readonly': readonly,
                   'DatePickerInput--invalid': invalid || !!errors.length,
                   'DatePickerInput--opened': opened,
                   'DatePickerInput--highlight': isHighlighted,
                   'DatePickerInput--float-label': floatLabel,
                 }">
    <template v-if="$slots.default || label" #label>
      <slot v-if="$slots.default" />
      <template v-else>{{ label }}</template>
    </template>
    <div ref="wrapperRef" class="DatePickerInput__input">
      <label v-if="$slots.prepend" :for="name">
        <slot name="prepend" />
      </label>
      <div class="DatePickerInput__wrapper">
        <input type="hidden"
               :name="name"
               :value="modelValue"
        />
        <input ref="inputRef"
               type="text"
               :value="isShownPrefixDate ? addPrefix((inputValue)) : inputValue"
               :disabled="disabled || readonly"
               :placeholder="placeholder"
               @input="onHandleInput"
               @focus="onHandleFocus"
               @blur="onHandleBlur"
        />
        <button type="button"
                class="DatePickerInput__toggle"
                :disabled="disabled || readonly"
                @click="onToggle">
          <Icon name="calendar" />
        </button>
      </div>
      <label v-if="$slots.append" :for="name">
        <slot name="append" />
      </label>
    </div>
    <div v-show="opened" ref="calendarRef" data-vc-input data-vc-position="bottom" />
  </InputSkeleton>
</template>

<script setup lang="ts">
  import { onClickOutside } from '@vueuse/core';
  import dayjs from 'dayjs';
  import Inputmask from 'inputmask';
  import { onMounted, computed, ref, watch, onUnmounted, toRefs, useTemplateRef } from 'vue';
  import { UiSize } from '../../../constants';
  import Icon from '../../Icon.vue';
  import InputSkeleton from '../InputSkeleton.vue';
  import { useDatePicker, FORMAT_DATE_RU } from './date-picker/use-date-picker';
  import { InputErrorObject } from './types';
  import './date-picker/date-picker.scss';

  type Props = {
    modelValue: string | string[];
    name: string;
    label?: string;
    size?: UiSize;
    placeholder?: string;
    invalid?: boolean;
    errors?: InputErrorObject[];
    disabled?: boolean;
    readonly?: boolean;
    highlight?: boolean;
    isPrefixDate?: boolean;
    dateMin?: string;
    dateMax?: string;
    isHiddenAfterSelect?: boolean; // eslint-disable-line vue/no-unused-properties
    floatLabel?: boolean;
    multiple?: boolean;
  };

  interface Days {
    currentDay: string;
    prevDay: string;
    nextDay: string;
    afterTomorrow: string;
  }

  const props = withDefaults(defineProps<Props>(), {
    errors: () => [],
    size: UiSize.MD,
    isHiddenAfterSelect: true,
    floatLabel: false,
    placeholder: '',
  });

  const emit = defineEmits<{
    (e: 'update:model-value', modelValue: string): void;
  }>();

  const { dateMin, dateMax } = toRefs(props);

  const isFocused = ref<boolean>(false);
  const inputMask = ref<Inputmask.Instance>();

  const inputRef = useTemplateRef<HTMLInputElement>('inputRef');
  const wrapperRef = useTemplateRef<HTMLDivElement>('wrapperRef');
  const calendarRef = useTemplateRef<HTMLDivElement>('calendarRef');

  onClickOutside(wrapperRef, () => {
    hideDatePicker();
  }, {
    ignore: [calendarRef],
  });

  const getDays = (): Days => {
    const now = dayjs();
    const currentDay = now.format(FORMAT_DATE_RU);
    const prevDay = now.subtract(1, 'day').format(FORMAT_DATE_RU);
    const nextDay = now.add(1, 'day').format(FORMAT_DATE_RU);
    const afterTomorrow = now.add(2, 'day').format(FORMAT_DATE_RU);

    return { currentDay, prevDay, nextDay, afterTomorrow };
  };

  const addPrefix = (date: string): string => {
    const { currentDay, prevDay, nextDay, afterTomorrow } = getDays();

    switch (date) {
      case currentDay:
        return `сегодня, ${date}`;

      case prevDay:
        return `вчера, ${date}`;

      case nextDay:
        return `завтра, ${date}`;

      case afterTomorrow:
        return `послезавтра, ${date}`;

      default:
        return date;
    }
  };

  const {
    opened,
    inputValue,
    showDatePicker,
    hideDatePicker,
    initDatePicker,
    updateDatePicker,
    destroyDatePicker,
    checkDateValidity,
  } = useDatePicker({ props, emit });

  const isHighlighted = computed(() => Boolean(props.highlight && !props.modelValue));
  const isShownPrefixDate = computed(() => props.isPrefixDate && !opened.value && !isFocused.value);

  const onToggle = () => {
    if (props.disabled) {
      return;
    }

    inputRef.value.focus();
  };

  const onHandleInput = (e: Event) => {
    const { value } = e.target as HTMLInputElement;

    if (props.multiple && value) {
      const isValid = value.split(', ')
        .every((date) => date.length === FORMAT_DATE_RU.length && checkDateValidity(date));

      if (isValid) {
        inputValue.value = value;
        updateDatePicker();
      }
    } else if (value.length === FORMAT_DATE_RU.length) {
      const isValid = checkDateValidity(value);

      if (isValid) {
        inputValue.value = value;
        updateDatePicker();
      }
    }
  };

  const maskEnable = () => {
    const options: Inputmask.Options = {
      showMaskOnFocus: false,
      placeholder: ' ',
      showMaskOnHover: false,
    };

    options.alias = 'datetime';
    options.inputFormat = 'dd.MM.yyyy';
    inputMask.value = new Inputmask(options).mask(inputRef.value);
  };

  const maskDestroy = () => {
    inputMask.value.remove();
  };

  const onHandleFocus = () => {
    if (!props.multiple) {
      maskEnable();
    }

    showDatePicker();

    isFocused.value = true;
  };

  const onHandleBlur = () => {
    if (!props.multiple) {
      maskDestroy();
    }

    isFocused.value = false;
  };

  onMounted(() => {
    initDatePicker(calendarRef.value);
  });

  onUnmounted(destroyDatePicker);

  watch(() => props.modelValue, (value: string | string[]) => {
    if (value && Array.isArray(value)
      && value.every((d) => /^\d{4}-(0?[1-9]|1[012])-(0?[1-9]|[12][0-9]|3[01])$/.test(d))) {
      // eslint-disable-next-line no-console
      console.warn(`DatePickerInput ${props.name}: modelValue должен быть массивом ISO-строк, а не массивом YYYY-MM-DD`);
    } else if (value && typeof value === 'string' && /^\d{4}-(0?[1-9]|1[012])-(0?[1-9]|[12][0-9]|3[01])$/.test(value)) {
      // eslint-disable-next-line no-console
      console.warn(`DatePickerInput ${props.name}: modelValue должен быть ISO-строкой, а не YYYY-MM-DD`);
    }
  }, { immediate: true });

  watch([dateMin, dateMax], () => {
    initDatePicker(calendarRef.value);
  });
</script>

<style lang="scss" scoped>
  @import '../../../assets/scss/_variables.scss';
  @import '../../../assets/scss/_mixins.scss';

  .DatePickerInput {
    position: relative;

    input {
      @include transition($transition-function, $transition-duration, border-color);

      width: 100%;
      border: 1px solid $color-grey-75;
      border-radius: 4px;
      background-color: $color-white;
      line-height: 1;
      font-size: 14px;
      text-overflow: ellipsis;

      &[type='number']::-webkit-inner-spin-button {
        appearance: textfield;
      }

      &[type='date']::-webkit-calendar-picker-indicator {
        width: 18px;
        height: 18px;
        padding: 0;
        background-image: url('../../../assets/sprite/calendar.svg');
        cursor: pointer;
      }
    }

    &--sm input {
      // ...
    }

    &--md input {
      height: 30px;
      padding-left: 10px;
      padding-right: 10px;
    }

    &--lg input {
      height: 40px;
      padding-left: 10px;
      padding-right: 10px;
    }

    &--xl input {
      height: 50px;
      padding-left: 15px;
      padding-right: 15px;
    }

    &--highlight input {
      background-color: #ffffd2;
    }

    &--disabled input,
    &--disabled .DatePickerInput__calendar {
      background-color: $color-grey-50;
    }

    &--disabled input,
    &--disabled .DatePickerInput__calendar,
    &--disabled .DatePickerInput__toggle {
      cursor: not-allowed;
    }

    input:focus {
      outline: none;
      border-color: $color-second;
    }

    input::placeholder {
      color: $color-grey-100;
    }

    &__toggle {
      cursor: pointer;
    }

    &__input > div {
      width: 100%;
      height: 100%;
    }

    &__wrapper {
      @include transition($transition-function, $transition-duration, border-color);

      height: 100%;
      display: flex;
      justify-content: center;
      border-width: 1px;
      border-style: solid;
      border-color: #d8dce2;
      border-radius: 4px;
      background-color: $color-white;
      overflow: auto;
    }

    &--disabled &__wrapper {
      cursor: not-allowed;
      user-select: none;
      background-color: $color-grey-50;

      &:focus-within {
        border-color: $color-grey-75;
      }
    }

    &__wrapper input {
      border-color: transparent;
      border-radius: 0;
      line-height: 24px;
    }

    &__wrapper:focus-within input {
      border-color: transparent;
    }

    &__toggle {
      @include transition($transition-function, $transition-duration, border-left-color);

      display: flex;
      align-items: center;
      justify-content: center;
      width: 29px;
      height: auto;
      border: none;
      border-top-right-radius: 4px;
      border-bottom-right-radius: 4px;
      background-color: transparent;
    }

    &__calendar {
      background-color: inherit;

      &:deep(svg.svg-icon) {
        width: 18px;
        height: 19px;
        vertical-align: middle;
      }
    }

    &__wrapper:focus-within &__toggle {
      border-left-color: $color-second;
    }

    &--disabled &__wrapper:focus-within &__toggle {
      border-left-color: $color-grey-75 ;
    }

    .svg-icon {
      @include transition($transition-function, $transition-duration, transform);

      width: 20px;
      height: 20px;
      color: $color-grey-100;
    }

    &--invalid input,
    &--invalid &__wrapper {
      border-color: $color-red-100;
    }

    &--invalid input::placeholder {
      color: $color-red-100;
    }

    &--invalid .svg-icon {
      color: $color-red-100;
    }

    &--float-label {
      @include float-label('DatePickerInput', unquote('input:nth-child(2)'));
    }
  }
</style>
